import { Button, Flex, Modal } from "antd";
import { Trans } from "react-i18next";
import { HEADER_HEIGHT } from "../constants";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useAntTheme } from "hooks/useAntTheme/";
import { useGo, useTranslate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { useTemplateData } from "../hooks/useTemplateData";
import { useCallback } from "react";
import { useTemplatesStatusStore } from "../store/useTemplatesStatusStore";
import { useItemCopyPaste } from "../hooks/useItemCopyPaste";
import { useFontGlyphWarning } from "../hooks/useFontGlyphWarning";
import { GenerateMediaButton } from "../../GenerateMediaButton";
import { calculateEstimatedGenerationTime } from "../helpers";
import { EditorToolbar } from "./common/EditorToolbar";
import { getProjectDetailRoute } from "pages/media/projects/helpers/helper";

type StoryboardHeaderProps = {
  onBeforeLeaving: () => Promise<void>;
  previewWidth: number;
  previewHeight: number;
};

export const StoryboardHeader = ({
  onBeforeLeaving,
  previewWidth,
  previewHeight,
}: StoryboardHeaderProps) => {
  const { theme } = useAntTheme();
  const t = useTranslate();

  const go = useGo();
  const navigate = useNavigate();

  const {
    projectId,
    instanceId,
    templatesData: {
      storyboardId,
      projectMedia,
      isFlashProject,
      projectType,
      storyboard,
      storyboardSettingsId,
    },
    storyboardWithSlidesData: { sections },
  } = useTemplateData();

  const isStoryboardReviewing = storyboard?.status === "Reviewing";

  const { updates, addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    updates: state.updates,
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const handleUndoRedo = useCallback(() => {
    if (updates === 0) {
      addTemplateUpdate();
    }
  }, [addTemplateUpdate, updates]);

  const { handleCopyItem, handlePasteItem, canCopy, canPaste } =
    useItemCopyPaste({ previewWidth, previewHeight });

  const { hasUnsupported, fontNames } = useFontGlyphWarning();
  const [modal, modalContextHolder] = Modal.useModal();

  // Before generating, if some text can't be rendered by its font, ask the user
  // to confirm
  const confirmBeforeGenerate = useCallback(() => {
    if (!hasUnsupported) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t("components.templates.fontWarning.title"),
        content: (
          <Trans components={{ bold: <strong /> }}>
            {t("components.templates.fontWarning.message", {
              count: fontNames.length,
              fonts: fontNames.join(", "),
            })}
          </Trans>
        ),
        okText: t("components.templates.fontWarning.generateAnyway"),
        cancelText: t("components.templates.fontWarning.cancel"),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }, [hasUnsupported, fontNames, modal, t]);

  if (!projectId) return;

  const projectDetailRoute = getProjectDetailRoute(
    projectId,
    projectType,
    instanceId
  );

  return (
    <Flex
      justify="space-between"
      align="center"
      style={{
        width: "100%",
        position: "absolute",
        height: HEADER_HEIGHT,
        backgroundColor: theme.colorBgBase,
        zIndex: 999,
        top: 0,
        left: 0,
        paddingInlineStart: 24,
        paddingInlineEnd: 24,
        borderBottom: `1px solid ${theme.colorBorderSecondary}`,
      }}
    >
      <Button
        icon={<ArrowLeftOutlined />}
        type="primary"
        shape="round"
        onClick={() => {
          void onBeforeLeaving().then(() => {
            go({ to: projectDetailRoute });
          });
        }}
        style={{
          color: "#FFFFFF",
          backgroundColor: "#0F172A",
          fontFamily: "Inter, sans-serif",
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "16px",
        }}
      >
        {t("components.templates.index.back")}
      </Button>
      <EditorToolbar
        onCopy={handleCopyItem}
        onPaste={handlePasteItem}
        canCopy={canCopy}
        canPaste={canPaste}
        onUndoRedo={handleUndoRedo}
      />
      <GenerateMediaButton
        projectId={projectId}
        mediaGenerationMode={projectMedia?.media_generation_mode}
        storyboardId={storyboardId ?? undefined}
        storyboardSettingsId={storyboardSettingsId ?? undefined}
        storyboardStatus={storyboard?.status}
        mediaId={projectMedia?.id ?? undefined}
        estimatedGenerationTime={calculateEstimatedGenerationTime(sections)}
        trackingPage="Storyboard"
        onBeforeGenerate={onBeforeLeaving}
        confirmBeforeGenerate={confirmBeforeGenerate}
        onSuccess={(approvalState) =>
          navigate(projectDetailRoute, { state: approvalState })
        }
        text={t(
          isFlashProject && !isStoryboardReviewing
            ? "components.templates.index.regenerateMedia"
            : "components.templates.index.generateMedia"
        )}
      />
      {modalContextHolder}
    </Flex>
  );
};
